import { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { llcApi, achApi, payeeApi, achTemplateApi } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Plus, Trash2, Pencil, Send, Clock, ArrowRightLeft, CheckCircle, XCircle, RotateCcw, Printer, Save, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { ACHPrintView } from '../components/ACHPrintView';

const SEC_CODES = [
  { value: 'CCD', label: 'CCD — Corporate Credit/Debit' },
  { value: 'PPD', label: 'PPD — Prearranged Payment & Deposit' },
  { value: 'WEB', label: 'WEB — Internet-Initiated' },
  { value: 'TEL', label: 'TEL — Telephone-Initiated' },
];

const ACCT_TYPES = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
];

const emptyForm = {
  llc_id: '', beneficiary_name: '', beneficiary_address: '', beneficiary_city_state_zip: '',
  beneficiary_bank_name: '', beneficiary_routing: '', beneficiary_account: '',
  beneficiary_account_type: 'checking', amount: '', sec_code: 'CCD',
  effective_date: new Date().toISOString().split('T')[0], purpose: '', reference: '', addenda: '',
};

export const ACHTransfersPage = () => {
  const [llcs, setLlcs] = useState([]);
  const [payees, setPayees] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printTransfer, setPrintTransfer] = useState(null);
  const printRef = useRef();

  useEffect(() => { fetchData(); }, []);

  const handlePrint = useReactToPrint({ contentRef: printRef });

  const fetchData = async () => {
    try {
      const [llcsRes, achRes, payeesRes, tplRes] = await Promise.all([
        llcApi.list(), achApi.list(), payeeApi.list(), achTemplateApi.list()
      ]);
      setLlcs(llcsRes.data);
      setTransfers(achRes.data);
      setPayees(payeesRes.data);
      setTemplates(tplRes.data);
      if (llcsRes.data.length > 0 && !formData.llc_id) {
        setFormData(prev => ({ ...prev, llc_id: llcsRes.data[0].id }));
      }
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  const formatCurrency = (v) => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  const getLLCName = (id) => llcs.find(l => l.id === id)?.company_name || 'Unknown';

  const handlePayeeChange = (payeeId) => {
    const payee = payees.find(p => p.id === payeeId);
    if (payee) {
      setFormData(prev => ({
        ...prev,
        beneficiary_name: payee.name,
        beneficiary_address: payee.address_line1 || '',
        beneficiary_city_state_zip: payee.city_state_zip || '',
        beneficiary_bank_name: payee.bank_name || '',
        beneficiary_routing: payee.bank_routing || '',
        beneficiary_account: payee.bank_account || '',
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.llc_id || !formData.beneficiary_name || !formData.beneficiary_routing || !formData.beneficiary_account || !formData.amount) {
      toast.error('Fill in all required fields'); return;
    }
    try {
      const payload = { ...formData, amount: parseFloat(formData.amount) };
      if (editingId) {
        await achApi.update(editingId, payload);
        toast.success('ACH transfer updated');
      } else {
        await achApi.create(payload);
        toast.success('ACH transfer created');
      }
      setDialogOpen(false);
      setEditingId(null);
      setFormData(emptyForm);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setFormData({
      llc_id: t.llc_id, beneficiary_name: t.beneficiary_name,
      beneficiary_address: t.beneficiary_address, beneficiary_city_state_zip: t.beneficiary_city_state_zip,
      beneficiary_bank_name: t.beneficiary_bank_name, beneficiary_routing: t.beneficiary_routing,
      beneficiary_account: t.beneficiary_account, beneficiary_account_type: t.beneficiary_account_type,
      amount: t.amount, sec_code: t.sec_code, effective_date: t.effective_date,
      purpose: t.purpose, reference: t.reference, addenda: t.addenda,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try { await achApi.delete(id); toast.success('ACH deleted'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const handleStatusChange = async (id, status) => {
    try { await achApi.updateStatus(id, status); toast.success(`ACH marked as ${status}`); fetchData(); }
    catch { toast.error('Failed to update status'); }
  };

  // Template functions
  const loadTemplate = (templateId) => {
    const tpl = templates.find(t => t.id === templateId);
    if (tpl) {
      setFormData(prev => ({
        ...prev,
        beneficiary_name: tpl.beneficiary_name,
        beneficiary_address: tpl.beneficiary_address || '',
        beneficiary_city_state_zip: tpl.beneficiary_city_state_zip || '',
        beneficiary_bank_name: tpl.beneficiary_bank_name || '',
        beneficiary_routing: tpl.beneficiary_routing,
        beneficiary_account: tpl.beneficiary_account,
        beneficiary_account_type: tpl.beneficiary_account_type || 'checking',
        sec_code: tpl.sec_code || 'CCD',
        purpose: tpl.purpose || '',
      }));
      toast.success(`Loaded template "${tpl.name}"`);
    }
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) { toast.error('Enter a template name'); return; }
    const data = {
      name: templateName,
      beneficiary_name: formData.beneficiary_name,
      beneficiary_address: formData.beneficiary_address,
      beneficiary_city_state_zip: formData.beneficiary_city_state_zip,
      beneficiary_bank_name: formData.beneficiary_bank_name,
      beneficiary_routing: formData.beneficiary_routing,
      beneficiary_account: formData.beneficiary_account,
      beneficiary_account_type: formData.beneficiary_account_type,
      sec_code: formData.sec_code,
      purpose: formData.purpose,
    };
    try {
      if (editingTemplateId) {
        await achTemplateApi.update(editingTemplateId, data);
        toast.success('Template updated');
      } else {
        await achTemplateApi.create(data);
        toast.success('Template saved');
      }
      setTemplateDialogOpen(false);
      setTemplateName('');
      setEditingTemplateId(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save template'); }
  };

  const deleteTemplate = async (id) => {
    try { await achTemplateApi.delete(id); toast.success('Template deleted'); fetchData(); }
    catch { toast.error('Failed to delete template'); }
  };

  const openPrintView = (transfer) => {
    setPrintTransfer(transfer);
    setPrintDialogOpen(true);
  };

  const statusBadge = (s) => {
    const styles = { pending: 'bg-amber-100 text-amber-800', sent: 'bg-green-100 text-green-800', returned: 'bg-red-100 text-red-800', cancelled: 'bg-slate-100 text-slate-600' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[s] || styles.pending}`}>{s}</span>;
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData({ ...emptyForm, llc_id: llcs[0]?.id || '' });
    setDialogOpen(true);
  };

  if (loading) return <div className="p-6 text-center text-slate-400">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="ach-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">ACH Transfers</h1>
          <p className="text-sm text-slate-500">Manage ACH direct debits and credits</p>
        </div>
        <Button onClick={openCreate} data-testid="create-ach-btn">
          <Plus className="w-4 h-4 mr-2" /> New ACH Transfer
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total ACH', value: transfers.length, color: 'slate' },
          { label: 'Pending', value: transfers.filter(t => t.status === 'pending').length, color: 'amber' },
          { label: 'Sent', value: transfers.filter(t => t.status === 'sent').length, color: 'green' },
          { label: 'Total Amount', value: formatCurrency(transfers.reduce((s, t) => s + t.amount, 0)), color: 'blue', isText: true },
        ].map(({ label, value, color, isText }) => (
          <Card key={label} className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">{label}</p>
              <p className={`text-lg font-semibold text-${color}-700`}>{isText ? value : value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Transfer List */}
      <Card className="border-slate-200">
        <CardHeader className="rounded-t-lg panel-header-themed">
          <CardTitle className="flex items-center gap-2 text-sm" style={{ color: '#ffffff' }}>
            <ArrowRightLeft className="w-4 h-4" /> ACH Transfer History ({transfers.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {transfers.length === 0 ? (
            <div className="p-8 text-center text-slate-400">No ACH transfers yet. Click "New ACH Transfer" to create one.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-3 text-xs text-slate-500 font-medium">Date</th>
                    <th className="text-left p-3 text-xs text-slate-500 font-medium">LLC</th>
                    <th className="text-left p-3 text-xs text-slate-500 font-medium">Beneficiary</th>
                    <th className="text-left p-3 text-xs text-slate-500 font-medium">Bank</th>
                    <th className="text-left p-3 text-xs text-slate-500 font-medium">SEC</th>
                    <th className="text-right p-3 text-xs text-slate-500 font-medium">Amount</th>
                    <th className="text-center p-3 text-xs text-slate-500 font-medium">Status</th>
                    <th className="text-right p-3 text-xs text-slate-500 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.map(t => (
                    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedTransfer(t)} data-testid={`ach-row-${t.id}`}>
                      <td className="p-3 text-slate-600">{new Date(t.effective_date).toLocaleDateString()}</td>
                      <td className="p-3 text-slate-900 font-medium text-xs">{getLLCName(t.llc_id)}</td>
                      <td className="p-3 text-slate-900">{t.beneficiary_name}</td>
                      <td className="p-3 text-slate-500 text-xs">{t.beneficiary_bank_name || '—'}</td>
                      <td className="p-3 text-slate-500 font-mono text-xs">{t.sec_code}</td>
                      <td className="p-3 text-right font-mono font-semibold text-slate-900">{formatCurrency(t.amount)}</td>
                      <td className="p-3 text-center">{statusBadge(t.status)}</td>
                      <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {t.status === 'pending' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleStatusChange(t.id, 'sent')} title="Mark Sent" data-testid={`ach-send-${t.id}`}>
                                <Send className="w-3.5 h-3.5 text-green-600" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(t)} title="Edit" data-testid={`ach-edit-${t.id}`}>
                                <Pencil className="w-3.5 h-3.5 text-slate-500" />
                              </Button>
                            </>
                          )}
                          {t.status === 'sent' && (
                            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(t.id, 'returned')} title="Mark Returned" data-testid={`ach-return-${t.id}`}>
                              <RotateCcw className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openPrintView(t)} title="Print" data-testid={`ach-print-row-${t.id}`}>
                            <Printer className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                          {t.status !== 'sent' && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" title="Delete" data-testid={`ach-delete-${t.id}`}>
                                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete ACH to {t.beneficiary_name}?</AlertDialogTitle>
                                  <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(t.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail View */}
      <Dialog open={!!selectedTransfer} onOpenChange={(o) => { if (!o) setSelectedTransfer(null); }}>
        <DialogContent className="max-w-lg" data-testid="ach-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-slate-600" />
              ACH Transfer Details
            </DialogTitle>
          </DialogHeader>
          {selectedTransfer && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-slate-500 block text-xs">Beneficiary</span><span className="font-semibold">{selectedTransfer.beneficiary_name}</span></div>
                <div><span className="text-slate-500 block text-xs">Amount</span><span className="font-mono font-bold text-lg">{formatCurrency(selectedTransfer.amount)}</span></div>
                <div><span className="text-slate-500 block text-xs">Beneficiary Routing</span><span className="font-mono">{selectedTransfer.beneficiary_routing}</span></div>
                <div><span className="text-slate-500 block text-xs">Beneficiary Account</span><span className="font-mono">{selectedTransfer.beneficiary_account} ({selectedTransfer.beneficiary_account_type})</span></div>
                <div><span className="text-slate-500 block text-xs">Originator</span><span>{selectedTransfer.originator_name}</span></div>
                <div><span className="text-slate-500 block text-xs">Originator Account</span><span className="font-mono">{selectedTransfer.originator_account}</span></div>
                <div><span className="text-slate-500 block text-xs">SEC Code</span><span className="font-mono">{selectedTransfer.sec_code}</span></div>
                <div><span className="text-slate-500 block text-xs">Effective Date</span><span>{new Date(selectedTransfer.effective_date).toLocaleDateString()}</span></div>
                <div><span className="text-slate-500 block text-xs">Status</span>{statusBadge(selectedTransfer.status)}</div>
                <div><span className="text-slate-500 block text-xs">LLC</span><span>{getLLCName(selectedTransfer.llc_id)}</span></div>
              </div>
              {selectedTransfer.purpose && <div><span className="text-slate-500 text-xs">Purpose:</span> {selectedTransfer.purpose}</div>}
              {selectedTransfer.reference && <div><span className="text-slate-500 text-xs">Reference:</span> {selectedTransfer.reference}</div>}
              {selectedTransfer.addenda && <div><span className="text-slate-500 text-xs">Addenda:</span> {selectedTransfer.addenda}</div>}
              <div className="flex justify-end pt-2 border-t border-slate-200">
                <Button variant="outline" size="sm" onClick={() => { setSelectedTransfer(null); openPrintView(selectedTransfer); }} data-testid="ach-print-btn">
                  <Printer className="w-4 h-4 mr-2" /> Print / View
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setEditingId(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ach-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit' : 'New'} ACH Transfer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Template Row */}
            <div className="flex items-end gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Load from Template</Label>
                <Select value="__none__" onValueChange={loadTemplate}>
                  <SelectTrigger data-testid="ach-template-select"><SelectValue placeholder="Select a saved template..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" disabled>Select a saved template...</SelectItem>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name} — {t.beneficiary_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.beneficiary_name && formData.beneficiary_routing && (
                <Button variant="outline" size="sm" onClick={() => {
                  setTemplateName(formData.beneficiary_name);
                  setEditingTemplateId(null);
                  setTemplateDialogOpen(true);
                }} data-testid="save-template-btn">
                  <Save className="w-3.5 h-3.5 mr-1" /> Save as Template
                </Button>
              )}
              {templates.length > 0 && (
                <Button variant="ghost" size="sm" className="text-red-500" onClick={() => {
                  // Show simple template management
                  const tpl = templates[0];
                  if (tpl) deleteTemplate(tpl.id);
                }} title="Manage templates">
                  <FileDown className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sending LLC *</Label>
                <Select value={formData.llc_id} onValueChange={(v) => setFormData(p => ({ ...p, llc_id: v }))}>
                  <SelectTrigger data-testid="ach-llc-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {llcs.map(l => <SelectItem key={l.id} value={l.id}>{l.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>From Payee Directory</Label>
                <Select value="__none__" onValueChange={handlePayeeChange}>
                  <SelectTrigger data-testid="ach-payee-select"><SelectValue placeholder="Fill from payee..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" disabled>Fill from payee...</SelectItem>
                    {payees.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Beneficiary Name *</Label><Input value={formData.beneficiary_name} onChange={(e) => setFormData(p => ({ ...p, beneficiary_name: e.target.value }))} data-testid="ach-beneficiary-name" /></div>
              <div><Label>Beneficiary Bank Name</Label><Input value={formData.beneficiary_bank_name} onChange={(e) => setFormData(p => ({ ...p, beneficiary_bank_name: e.target.value }))} data-testid="ach-bank-name" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Routing Number *</Label><Input value={formData.beneficiary_routing} onChange={(e) => setFormData(p => ({ ...p, beneficiary_routing: e.target.value }))} className="font-mono" data-testid="ach-routing" /></div>
              <div><Label>Account Number *</Label><Input value={formData.beneficiary_account} onChange={(e) => setFormData(p => ({ ...p, beneficiary_account: e.target.value }))} className="font-mono" data-testid="ach-account" /></div>
              <div>
                <Label>Account Type</Label>
                <Select value={formData.beneficiary_account_type} onValueChange={(v) => setFormData(p => ({ ...p, beneficiary_account_type: v }))}>
                  <SelectTrigger data-testid="ach-acct-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACCT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Amount *</Label><Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))} className="font-mono" data-testid="ach-amount" /></div>
              <div>
                <Label>SEC Code</Label>
                <Select value={formData.sec_code} onValueChange={(v) => setFormData(p => ({ ...p, sec_code: v }))}>
                  <SelectTrigger data-testid="ach-sec-code"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEC_CODES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Effective Date</Label><Input type="date" value={formData.effective_date} onChange={(e) => setFormData(p => ({ ...p, effective_date: e.target.value }))} data-testid="ach-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Purpose</Label><Input value={formData.purpose} onChange={(e) => setFormData(p => ({ ...p, purpose: e.target.value }))} data-testid="ach-purpose" /></div>
              <div><Label>Reference</Label><Input value={formData.reference} onChange={(e) => setFormData(p => ({ ...p, reference: e.target.value }))} data-testid="ach-reference" /></div>
            </div>
            <div><Label>Addenda</Label><Input value={formData.addenda} onChange={(e) => setFormData(p => ({ ...p, addenda: e.target.value }))} placeholder="Optional addenda record" data-testid="ach-addenda" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditingId(null); }}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="ach-submit-btn">{editingId ? 'Update' : 'Create'} ACH Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle>Save ACH Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Template Name *</Label>
              <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="e.g. Monthly rent to FedEx" data-testid="template-name-input" />
            </div>
            <p className="text-xs text-slate-400">Saves beneficiary, bank, SEC code, and purpose fields for reuse.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveAsTemplate} data-testid="template-save-confirm">Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ACH Print Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ach-print-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>ACH Transfer Document</span>
              <Button onClick={handlePrint} size="sm" data-testid="ach-print-execute">
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          <ACHPrintView ref={printRef} ach={printTransfer} llc={llcs.find(l => l.id === printTransfer?.llc_id)} />
        </DialogContent>
      </Dialog>

      {/* Saved Templates List */}
      {templates.length > 0 && (
        <Card className="border-slate-200" data-testid="ach-templates-card">
          <CardHeader className="rounded-t-lg panel-header-themed">
            <CardTitle className="flex items-center gap-2 text-sm" style={{ color: '#ffffff' }}>
              <Save className="w-4 h-4" /> Saved ACH Templates ({templates.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-3 text-xs text-slate-500 font-medium">Template Name</th>
                  <th className="text-left p-3 text-xs text-slate-500 font-medium">Beneficiary</th>
                  <th className="text-left p-3 text-xs text-slate-500 font-medium">Bank</th>
                  <th className="text-left p-3 text-xs text-slate-500 font-medium">Routing</th>
                  <th className="text-left p-3 text-xs text-slate-500 font-medium">SEC</th>
                  <th className="text-right p-3 text-xs text-slate-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`template-row-${t.id}`}>
                    <td className="p-3 font-semibold text-slate-900">{t.name}</td>
                    <td className="p-3 text-slate-600">{t.beneficiary_name}</td>
                    <td className="p-3 text-slate-500 text-xs">{t.beneficiary_bank_name || '—'}</td>
                    <td className="p-3 text-slate-500 font-mono text-xs">{t.beneficiary_routing}</td>
                    <td className="p-3 font-mono text-xs">{t.sec_code}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => {
                          loadTemplate(t.id);
                          setDialogOpen(true);
                        }} title="Use template" data-testid={`use-template-${t.id}`}>
                          <Plus className="w-3.5 h-3.5 text-green-600" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" title="Delete template" data-testid={`delete-template-${t.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete template "{t.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>This will remove the saved template. Existing transfers are not affected.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteTemplate(t.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ACHTransfersPage;
